<template>
  <div class="container">
    <div class="form-header">
      <h1 class="title-screen">{{ adminText('organizationAll.subscribers_6f13df39', 'Subscribers') }}</h1>
      <a
        v-if="token"
        download
        :href="`${serverURL}/files/organization/${organizationSlug}/export/subscribers/xlsx/${token}`"
        class="export"
      >
        <img src="/assets/export-icon.svg" :alt="adminText('organizationAll.export_icon_1c4dcecc', 'export icon')" class="export-icon">
        <p class="export-text">{{ adminText('organizationAll.export_all_subscribers_4f6f372c', 'EXPORT ALL SUBSCRIBERS') }}</p>
      </a>
    </div>
    <div v-if="!$apollo.loading" class="attendee_table">
      <selected-items-header
        v-if="selected.length"
        :selected="selected"
        name="subscriber"
        @clear="selected = []"
        @delete="showDeleteDialog = true"
      />
      <div
        v-if="subscribers.length === 0 && !loading"
        class="no-attendees-message-container border__left border__right"
      >
        <span class="no-attendees-message">{{ adminText('organizationAll.no_matching_records_found_0c7f0a64', 'No matching records found.') }}</span>
      </div>
      <div v-if="loading">
        <spinner />
      </div>
      <template v-else>
        <table-list
          :selected.sync="selected"
          :organization-slug="organizationSlug"
          :attendees="subscribers"
          body-style="grid-auto-columns: 50px minmax(150px, 250px) !important;"
          :visible-fields="visibleFields"
          :attendee-fields="attendeeFields"
          :pagination="pagination"
          @click="
            selected = selected.includes($event)
              ? selected.filter(s => s !== $event)
              : [...selected, $event]
          "
          @sort="handleSort($event)"
        />
        <table-pagination
          :pagination="pagination"
          :total-attendees="totalSubscribersCount"
          @change="handlePagination($event)"
          @changePerPage="handlePagination($event)"
        />
      </template>
    </div>
    <delete-dialog
      :content="`${adminText('organizationAll.are_you_sure_you_want_to_delete_selected_subs_8e2fe92f', 'Are you sure you want to delete selected subscribers? This operation cannot be undone.')}`"
      :on-delete="deleteSelected"
      :show.sync="showDeleteDialog"
      :title="adminText('organizationAll.delete_subscribers_6b70678a', 'Delete subscribers')"
    />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import {
  GET_ORGANIZATION_SUBSCRIBERS,
  DELETE_SUBSCRIBERS,
} from '../../../graphql/subscribers.graphql'
import { serverURL } from '../../../utils'
import { getSubscribersExportToken } from '../../../http'
import deleteDialog from '../../shared/dialogs/delete-dialog.vue'
import tableList from '../../accreditations/attendee-table/list.vue'
import selectedItemsHeader from '../../tickets/selected-items-header.vue'
import tablePagination from '../../accreditations/attendee-table/pagination.vue'

export default {
  mixins: [OrganizationLocalization],
  name: 'subscribers',
  components: {
    deleteDialog,
    tableList,
    tablePagination,
    selectedItemsHeader,
  },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    const { pagination } = this.getInitialFromQueryParams()

    return {
      serverURL,
      subscribers: [],
      pagination,
      token: undefined,
      showDeleteDialog: false,
      selected: [],
      organizerId: null,
      attendeeFields: {},
      visibleFields: ['Name', 'Email'],
      totalSubscribersCount: 0,
      loading: false,
    }
  },
  async mounted() {
    await this.fetchSubscribers()
    await this.getExportToken()
  },
  methods: {
    async getExportToken() {
      const { data } = await getSubscribersExportToken(this.organizationSlug)
      if (!data) {
        // handle
      }
      this.token = data.token
    },
    async deleteSelected() {
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_SUBSCRIBERS,
        variables: {
          organizationSlug: this.organizationSlug,
          ids: this.selected.map(id => parseInt(id)),
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: 'Oops, something went wrong!',
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Successfully unsubscribed users!',
          color: 'success',
        }

        this.selected = []
        await this.fetchSubscribers()
      }
    },
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === 1) return
      if (page > currentPage && !(this.totalSubscribersCount - currentPerPage * currentPage > 0))
        return

      this.pagination = {
        ...this.pagination,
        per_page: per_page === 'All' ? -1 : per_page,
        page: page,
      }

      await this.addPaginationToRoute(this.pagination)
    },
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination
      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      await this.addPaginationToRoute(pagination)
      this.pagination = pagination
    },
    getInitialFromQueryParams() {
      const { per_page, page, sortBy, descending } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: parseInt(per_page, 10) || 10,
          sortBy: sortBy,
          descending: descending ? !!descending : false,
        },
      }
    },
    async addPaginationToRoute({ page, per_page, sortBy, descending }) {
      await this.$router.replace({
        query: {
          ...this.$route.query,
          per_page,
          page,
          sortBy,
          descending: descending ? 1 : 0,
        },
      })

      await this.fetchSubscribers()
    },
    async fetchSubscribers() {
      this.loading = true

      const {
        data: { getOrganizationSubscribers },
        errors,
      } = await this.$apollo.query({
        query: GET_ORGANIZATION_SUBSCRIBERS,
        variables: {
          organizationSlug: this.organizationSlug,
          pagination: this.pagination,
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: 'Oops, Something went wrong!',
          color: 'error',
        }
      } else {
        this.totalSubscribersCount = getOrganizationSubscribers.count

        this.subscribers =
          getOrganizationSubscribers.subscribers.map(e => {
            return {
              id: e.id,
              name: `${e.firstName} ${e.lastName}`,
              email: e.email,
            }
          }) || {}
      }

      this.loading = false
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 43.75rem;
}

.radio-item input[type='radio'] {
  display: none;
}

.radio-item label {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1.125rem;
  color: #979797;
}

.radio-item label:before {
  content: ' ';
  display: inline-block;
  position: relative;
  top: 0.313rem;
  margin: 0 0.313rem 0 0;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 0.688rem;
  border: 0.125rem solid #666666;
  background-color: transparent;
}

.radio-item input[type='radio']:checked + label:after {
  border-radius: 0.688rem;
  width: 0.75rem;
  height: 0.75rem;
  position: absolute;
  top: 0.563rem;
  left: 0.625rem;
  content: ' ';
  display: block;
  background: #0277bd;
}

.export-text {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 700;
  font-size: 1.125rem;
  color: #0277bd;
  margin-bottom: 0;
}

.export {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.export-icon {
  height: 1.125rem;
  width: 1.125rem;
  margin-right: 0.625rem;
}

.form-header {
  display: flex;
  justify-content: space-between;
}

@media screen and (max-width: 450px) {
  .form-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
